import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn, fmtNum } from '../utils.js';

/**
 * Tab state in the URL (`?tab=`), so a tab is linkable and survives a refresh.
 * Falls back to the first id for anything unrecognised.
 */
export function useTabParam<T extends string>(ids: readonly T[], param = 'tab'): [T, (id: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(param);
  const active = ids.includes(raw as T) ? (raw as T) : ids[0];
  return [
    active,
    (id: T) => {
      const next = new URLSearchParams(params);
      next.set(param, id);
      setParams(next, { replace: true });
    },
  ];
}

export interface TabDef<T extends string> {
  id: T;
  label: string;
  /** Optional badge — omitted entirely when undefined (0 still renders, and reads as "nothing here"). */
  count?: number;
  /** Draws the badge as a warning; for counts that mean "something needs attention". */
  alert?: boolean;
  /** Shown but not selectable — for a step that has nothing to show until an earlier one is done. */
  disabled?: boolean;
}

/**
 * Segmented tab bar. RTL-aware: the arrow keys follow the visual order, which is
 * reversed from the array order in an RTL document.
 */
export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  className,
}: {
  tabs: TabDef<T>[];
  active: T;
  onChange: (id: T) => void;
  className?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);


  const move = (delta: number) => {
    const enabled = tabs.filter((t) => !t.disabled);
    const idx = enabled.findIndex((t) => t.id === active);
    const next = enabled[(idx + delta + enabled.length) % enabled.length];
    if (!next) return;
    onChange(next.id);
    listRef.current?.querySelector<HTMLButtonElement>(`[data-tab="${next.id}"]`)?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      className={cn('flex flex-wrap items-center gap-1 rounded-xl border border-border bg-card p-1', className)}
      onKeyDown={(e) => {
        // In RTL, ArrowLeft moves forward through the array.
        const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
        if (e.key === 'ArrowLeft') { e.preventDefault(); move(rtl ? 1 : -1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); move(rtl ? -1 : 1); }
      }}
    >
      {tabs.map((t) => {
        const selected = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            data-tab={t.id}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            disabled={t.disabled}
            onClick={() => onChange(t.id)}
            className={cn(
              'flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
              selected
                ? 'bg-secondary font-semibold text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
            )}
          >
            {t.label}
            {t.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                  t.alert && t.count > 0
                    ? 'bg-destructive/15 text-destructive'
                    : selected
                      ? 'bg-foreground/10 text-muted-foreground'
                      : 'bg-muted text-muted-foreground',
                )}
              >
                {fmtNum(t.count)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
